import { TableCell, TableRow, Typography, IconButton } from "@mui/material";
import { memo } from "react";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import { useSignalRContext } from "../../../SignalR/useSignalR";

const PositionsRow = ({
    id,
    symbolName,
    tradeSide,
    volume,
    entryPrice,
    data,
    rowIdx,
    columnVisibility,
    isParent = false,
    isChild = false,
    isExpanded = false,
    onToggleExpand = null,
    childCount = 0,
    symbolInfo = null,
    magicId = '',           // NEW: Magic ID for identification
    openedAt = '--',        // NEW: Opened timestamp
    hasLots = false,        // NEW: Whether this row has expandable lots (Level 2 -> Level 3)
    isGrandchild = false    // NEW: Whether this is a lot row (Level 3)
}) => {
    const openPrice = data?.openPrice || 0;
    const lpName = data?.lpName || '';
    const lpId = data?.lpId || '';
    const accountNumber = data?.accountNumber || '';
    const { tickData } = useSignalRContext();

    // Get tick data for this symbol (has digits)
    const quote = tickData?.find(item => item?.symbolName === symbolName);

    // Use digits from tickData (real-time, more reliable), fallback to symbolInfo
    const symbolDigits = quote?.digits ?? symbolInfo?.digits ?? 5;

    // Use contractSize from symbolInfo (only source for this data)
    const contractSize = symbolInfo?.contractSize || 100000;

    /** Format price using symbol digits */
    const formatPrice = (price) => {
        if (!price) return '';
        const priceNum = parseFloat(price);
        if (isNaN(priceNum)) return price;
        return priceNum.toFixed(symbolDigits);
    };

    /** Format volume in lots */
    const formatVolume = (volume) => {
        if (!volume) return '';
        const volumeNum = parseFloat(volume);
        if (isNaN(volumeNum)) return volume;
        return volumeNum < 0.01 ? volumeNum.toFixed(6) : volumeNum.toFixed(2);
    };

    /** VALIDATION GATE 4: Calculate profit and pips with comprehensive validation */
    const calculateProfit = (openPrice, currentPrice, volume, side, conversionRate = 1) => {
        // CHECKPOINT: Validate input parameters
        if (!openPrice || !currentPrice || !volume) {
            return { profit: '0.00', profitPips: '0.0' };
        }

        const openPriceNum = parseFloat(openPrice);
        const currentPriceNum = parseFloat(currentPrice);
        const volumeNum = parseFloat(volume);
        const conversionRateNum = parseFloat(conversionRate) || 1;

        // CHECKPOINT: Validate numeric conversions
        if (isNaN(openPriceNum) || isNaN(currentPriceNum) || isNaN(volumeNum)) {
            console.warn('⚠️ PnL VALIDATION: Invalid numeric values', {
                openPrice, currentPrice, volume, side
            });
            return { profit: '0.00', profitPips: '0.0' };
        }

        // CHECKPOINT: Validate price values are reasonable
        if (openPriceNum <= 0 || currentPriceNum <= 0) {
            console.warn('⚠️ PnL VALIDATION: Non-positive price detected', {
                openPriceNum, currentPriceNum
            });
            return { profit: '0.00', profitPips: '0.0' };
        }

        const pipSize = 1 / Math.pow(10, symbolDigits - 1);
        const priceDiff = side === "Buy"
            ? (currentPriceNum - openPriceNum)
            : (openPriceNum - currentPriceNum);

        const profitPips = priceDiff / pipSize;
        const profitValue = priceDiff * contractSize * volumeNum * conversionRateNum;

        // CHECKPOINT: Log extreme profit values for review
        if (Math.abs(profitValue) > 100000) {
            console.warn('⚠️ PnL VALIDATION: Extreme profit value detected', {
                profitValue,
                openPriceNum,
                currentPriceNum,
                volumeNum,
                contractSize,
                side,
                symbolName
            });
        }

        return {
            profit: profitValue.toFixed(2),
            profitPips: profitPips.toFixed(1)
        };
    };

    let currentPrice = '';
    let profit = '0.00';
    let profitPips = '0.0';

    // Get conversion rate from data (defaults to 1 if not provided)
    const conversionRateToUsd = data?.conversionRateToUsd || 1;

    // quote is already declared above (line 28)
    if (quote) {
        const bid = parseFloat(quote.bid);
        const ask = parseFloat(quote.ask);
        const currentPriceValue = tradeSide === "Buy" ? bid : ask;
        currentPrice = formatPrice(currentPriceValue);

        if (isParent && data.childPositions && Array.isArray(data.childPositions)) {
            // Aggregate profit from child positions
            const aggregated = data.childPositions.reduce((acc, childPos) => {
                const childCurrentPrice = childPos.direction === "Buy" ? bid : ask;
                const childConversionRate = childPos.conversionRateToUsd || 1;
                const childResult = calculateProfit(
                    childPos.openPrice || childPos.entryPrice,
                    childCurrentPrice,
                    childPos.volume,
                    childPos.direction,
                    childConversionRate
                );

                return {
                    profit: (parseFloat(acc.profit) + parseFloat(childResult.profit)).toFixed(2),
                    profitPips: (parseFloat(acc.profitPips) + parseFloat(childResult.profitPips)).toFixed(1)
                };
            }, { profit: '0.00', profitPips: '0.0' });

            profit = aggregated.profit;
            profitPips = aggregated.profitPips;
        } else {
            const result = calculateProfit(
                data.openPrice || entryPrice,
                currentPriceValue,
                volume,
                tradeSide,
                conversionRateToUsd
            );
            profit = result.profit;
            profitPips = result.profitPips;
        }
    } else if (data.currentPrice) {
        currentPrice = formatPrice(data.currentPrice);

        if (!isParent) {
            const result = calculateProfit(
                data.openPrice || entryPrice,
                data.currentPrice,
                volume,
                tradeSide,
                conversionRateToUsd
            );
            profit = result.profit;
            profitPips = result.profitPips;
        }
    }

    let bgColor = rowIdx % 2 === 0 ? 'rgba(6, 182, 212, 0.10)' : '#181d23';
    if (isParent) bgColor = 'rgba(6, 182, 212, 0.20)';
    if (isChild && !isGrandchild) bgColor = 'rgba(0, 0, 0, 0.2)';
    if (isGrandchild) bgColor = 'rgba(0, 0, 0, 0.4)'; // Darker for lot rows

    const textStyle = {
        color: 'white',
        fontWeight: isParent ? 500 : 400,
        fontSize: '11px',
        whiteSpace: 'nowrap',      // Prevent text wrapping
        overflow: 'hidden',         // Hide overflow text
        textOverflow: 'ellipsis'    // Show ellipsis for truncated text
    };

    // Color-coded PnL style (green for profit, red for loss)
    const profitValue = parseFloat(profit) || 0;
    const profitColor = profitValue >= 0 ? '#10b981' : '#ef4444'; // Green : Red
    const profitStyle = {
        ...textStyle,
        color: profitColor,
        fontWeight: 600  // Make PnL bold for emphasis
    };

    return (
        <TableRow
            sx={{
                backgroundColor: bgColor,
                borderLeft: isParent ? '3px solid rgba(6, 182, 212, 0.5)' : 'none',
                transition: 'background-color 0.15s ease-in-out',
                '&:hover': {
                    backgroundColor: isParent
                        ? 'rgba(6, 182, 212, 0.25)'
                        : isChild && !isGrandchild
                            ? 'rgba(6, 182, 212, 0.12)'
                            : isGrandchild
                                ? 'rgba(6, 182, 212, 0.08)'
                                : bgColor,
                    cursor: (isParent || (isChild && hasLots)) ? 'pointer' : 'default'
                }
            }}
        >
            {/* Column 1: Expand Icon - Enhanced visibility */}
            <TableCell align="left" sx={{ width: '40px', paddingLeft: isGrandchild ? '80px' : (isChild ? '40px' : '10px'), minWidth: '40px', maxWidth: '120px' }}>
                {(isParent || (isChild && hasLots)) ? (
                    <IconButton
                        size="small"
                        onClick={onToggleExpand}
                        sx={{
                            padding: '2px',
                            color: isExpanded ? '#06b6d4' : '#94a3b8', // Cyan when expanded, slate when collapsed
                            backgroundColor: isExpanded ? 'rgba(6, 182, 212, 0.15)' : 'rgba(148, 163, 184, 0.1)',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                backgroundColor: isExpanded ? 'rgba(6, 182, 212, 0.25)' : 'rgba(148, 163, 184, 0.2)',
                                color: isExpanded ? '#22d3ee' : '#cbd5e1',
                                transform: 'scale(1.1)',
                            },
                            border: isExpanded ? '1px solid rgba(6, 182, 212, 0.3)' : '1px solid rgba(148, 163, 184, 0.2)',
                        }}
                    >
                        {isExpanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
                    </IconButton>
                ) : (
                    <span style={{ width: '24px', display: 'inline-block' }}></span>
                )}
            </TableCell>

            {/* TIER 1: CRITICAL TRADER DATA */}
            {/* Column 2: Symbol - Add left padding for visual hierarchy */}
            <TableCell align="left" sx={{
                minWidth: '70px',
                maxWidth: '90px',
                paddingLeft: isGrandchild ? '16px' : (isChild ? '12px' : '8px')
            }}>
                <Typography sx={textStyle}>{symbolName}</Typography>
            </TableCell>

            {/* Column 3: Direction */}
            <TableCell align="left" sx={{ minWidth: '50px', maxWidth: '60px' }}>
                <Typography sx={textStyle}>{tradeSide}</Typography>
            </TableCell>

            {/* Column 4: Volume - RIGHT ALIGNED with visual indicator for expandable lots */}
            <TableCell align="right" sx={{ minWidth: '80px', maxWidth: '100px' }}>
                <Typography sx={{
                    ...textStyle,
                    // VISUAL CHECKPOINT: Highlight volume when there are multiple lots to expand
                    color: (hasLots && data?.lots?.length > 1) ? '#22d3ee' : textStyle.color,
                    fontWeight: (hasLots && data?.lots?.length > 1) ? 600 : textStyle.fontWeight,
                    cursor: (hasLots && data?.lots?.length > 1) ? 'pointer' : 'default'
                }} title={data?.volumeWithCount || formatVolume(volume)}>
                    {data?.volumeWithCount || formatVolume(volume)}
                    {/* VISUAL INDICATOR: Show + icon for expandable lots */}
                    {hasLots && data?.lots?.length > 1 && !isGrandchild && (
                        <span style={{
                            marginLeft: '4px',
                            fontSize: '10px',
                            color: '#06b6d4',
                            fontWeight: 'bold'
                        }}>⊕</span>
                    )}
                </Typography>
            </TableCell>

            {/* Column 5: Entry Price - RIGHT ALIGNED */}
            <TableCell align="right" sx={{ minWidth: '80px', maxWidth: '100px' }}>
                <Typography sx={textStyle}>{formatPrice(entryPrice)}</Typography>
            </TableCell>

            {/* Column 6: Current Price - RIGHT ALIGNED */}
            <TableCell align="right" sx={{ minWidth: '80px', maxWidth: '100px' }}>
                <Typography sx={textStyle}>{formatPrice(currentPrice)}</Typography>
            </TableCell>

            {/* Column 7: Profit ($) - COLOR CODED & RIGHT ALIGNED */}
            <TableCell align="right" sx={{ minWidth: '70px', maxWidth: '90px' }}>
                <Typography sx={profitStyle}>{profit}</Typography>
            </TableCell>

            {/* Column 8: Profit (Pips) - COLOR CODED & RIGHT ALIGNED */}
            <TableCell align="right" sx={{ minWidth: '70px', maxWidth: '90px' }}>
                <Typography sx={profitStyle}>{profitPips}</Typography>
            </TableCell>

            {/* TIER 2: IMPORTANT TRADER DATA */}
            {/* Column 9: Take Profit - RIGHT ALIGNED */}
            <TableCell align="right" sx={{ minWidth: '70px', maxWidth: '90px' }}>
                <Typography sx={textStyle}>{data?.takeProfit || ''}</Typography>
            </TableCell>

            {/* Column 10: Stop Loss - RIGHT ALIGNED */}
            <TableCell align="right" sx={{ minWidth: '70px', maxWidth: '90px' }}>
                <Typography sx={textStyle}>{data?.stopLoss || ''}</Typography>
            </TableCell>

            {/* Column 11: Opened At */}
            <TableCell align="left" sx={{ minWidth: '110px', maxWidth: '130px' }}>
                <Typography sx={textStyle} title={openedAt || data?.openedAt || '--'}>
                    {openedAt || data?.openedAt || '--'}
                </Typography>
            </TableCell>

            {/* TIER 3: TECHNICAL/ADMIN DATA */}
            {/* Column 12: AC# */}
            <TableCell align="left" sx={{ minWidth: '70px', maxWidth: '90px' }}>
                <Typography sx={textStyle}>{accountNumber}</Typography>
            </TableCell>

            {/* Column 13: Strategy ID */}
            <TableCell align="left" sx={{ minWidth: '60px', maxWidth: '80px' }}>
                <Typography sx={textStyle}>{id}</Typography>
            </TableCell>

            {/* Column 14: LP Name */}
            <TableCell align="left" sx={{ minWidth: '100px', maxWidth: '120px' }}>
                <Typography sx={textStyle} title={lpName}>
                    {lpName}
                </Typography>
            </TableCell>

            {/* Column 15: LP Id */}
            <TableCell align="left" sx={{ minWidth: '60px', maxWidth: '80px' }}>
                <Typography sx={textStyle}>{lpId}</Typography>
            </TableCell>

            {/* Column 16: Magic ID */}
            <TableCell align="left" sx={{ minWidth: '120px', maxWidth: '140px' }}>
                <Typography sx={textStyle} title={magicId || data?.magicId || '--'}>
                    {magicId || data?.magicId || '--'}
                </Typography>
            </TableCell>
        </TableRow>
    );
};

export default memo(PositionsRow);